// !!!! - DO NOT MODIFY - !!!!
// THIS FILE IS DEPLOYED AUTOMATICALLY BY MATTv2.
//
//  Deployment info:
//    Updated at:     2026-06-08T12:06:54+00:00
//    Cause:          Manual deployment.
//    Deployment ID:  192397
//    Project:        628
//    Environment:    8046 - master
//    MD5:            bdc4a7b7da383390c5101f8dfe65c9d8
//    Signature:      eyJpdiI6IlphRzBGdDNNN3RkNHRIbHAyRmtINlE9PSIsInZhbHVlIjoiNG9SVlIyOGlkbVg1dmNWUCtDaW9wVUl4THFNTXkxN1ZJaDhyZXU4Y2dieFJFd3BcL3ZNUmRETGV4YUQ2UGxEVnlVd1pGNnVERGEyUisxKzFBVGF4T3V4c1ZQUmNSWmVpQllzSlJqMjdkUVVxbWhwd1JtZTJUenFoK3VqNk01b0dMVUVEOWJSeFF3SDEyMVh6UVoyNDM5TGdnYVlWcWRhYVllTXNIVzlmSVIydDVoWnZZQlpuKzNmV2JwdFQ5eVZ0TENCVkRjSFRpaE83NERHMVlOQW9MSnBDc2ZYRU9YSVlVQmlxMmNEMk95WjRjXC9FcjNYQnp3bkpcL1ppNGw2Q2dMWnQxTTdrdzdoUlZXbURGY2hKQWNDRTY1TFB6UFJINFFXcE5oaGd0TWg5Y2pYOUJsZTZJdHVMSndsczBxcU12SW5JU1FLSG5OZ2lXTEZiRWdGbEd4aHYzdXdwZ2xiY3BKQVprNEQ3UU9GV1ZKTFVXak00VjJEQkQ5VkZcLzcrYUtzM2tKNHdMNXJFbWs5cmJic3BRZ2lzTUtTQ1EyME5uSHl1eFwvZFNMVklRcnUwPSIsIm1hYyI6IjIyZGM3ZjY2MWJiMTlhYTIyOTU5ZDdiYWQ4Nzg2ZGNlMDZkZjcwYjU4Nzg1YzZjZWY5MTBiOTcwMzIxZTMyZGUifQ==
// @ts-check
import { calculateRelativeRects } from "./frame-sizing.js";

/**
 * @template T
 * @param {T} predicate 
 * @param {string} msg 
 * @returns {asserts predicate}
 */
const invariant = (predicate, msg = 'Invariant failed') => {
    if (!predicate) {
        throw new Error(msg);
    }
}

/**
 * @typedef {Object} FramePreviewContext
 * @property {import("./component-frame-product").FrameContextVariant} variantContext
 * @property {boolean} landscape
 * @property {FramePreviewContextImage} userImage
 */
/**
 * @typedef {Object} FramePreviewContextImage
 * @property {string} url
 * @property {number} width
 * @property {number} height
 */

export class FramePreviewElement extends HTMLElement {
    #refs = {};

    /** @type {FramePreviewContext|undefined} */
    #previewContext;

    connectedCallback() {
        this.#refs = {
        };
    }

    /**
     * @param {FramePreviewContext} previewContext
     * @param {boolean} scalePreview
     */
    update(previewContext, scalePreview) {
        this.textContent = '';

        this.#previewContext = previewContext;
        
        if (scalePreview) {
            this.append(this.#renderScalePreview());
        } else {
            this.append(this.#renderStandardPreview());
        }
    }

    /**
     * @returns {HTMLElement}
     */
    #renderStandardPreview() {
        invariant(this.#previewContext);

        const relative = calculateRelativeRects(this.#previewContext.variantContext);

        const containerAspectRatio = 
            this.#previewContext.landscape
                ? `${this.#previewContext.variantContext.frameImage.height}/${this.#previewContext.variantContext.frameImage.width}`
                : `${this.#previewContext.variantContext.frameImage.width}/${this.#previewContext.variantContext.frameImage.height}`;


        // Create a container with the same aspect ratio as the frame-image
        const container = document.createElement('div');
        if (this.#previewContext.landscape) {
            container.style.width = '100cqmin';
        } else {
            container.style.height = '100cqmin';
        }
        container.style.aspectRatio = containerAspectRatio;
        container.style.position = 'relative';
        container.style.contain = 'strict';
        container.style.containerType = 'size';

        // The container bounds establish a %-coordinate system for the rest of the
        // preview elements, where (0%, 0%) is the top-left of the container/frame-image
        // and (100%, 100%) is the bottom-right of the container/frame-image
        //
        // This simplifies the rest of the calculations and means layout doesn't need recalculating
        // when the page/container is resized.

        // Add frame image
        // Covers (0%, 0%) (100%, 100%) in portait, but in landscape it'll need rotating
        const frameImage = document.createElement('img');
        frameImage.src = this.#previewContext.variantContext.frameImage.url;
        frameImage.style.position = 'absolute';
        frameImage.style.inset = '0';
        frameImage.style.width = '100%';
        frameImage.style.height = '100%';
        frameImage.style.zIndex = '2';

        if (this.#previewContext.landscape) {
            // Since transform doesn't affect layout, we'll need to account for the width/height swap
            // prior to rotating, which we do using a wrapping div with appropriate container query sizes
            const frameImageWrapper = document.createElement('div');
            frameImageWrapper.style.position = 'relative';
            frameImageWrapper.style.zIndex = '2';
            // swapping height and width
            frameImageWrapper.style.width = '100cqh';
            frameImageWrapper.style.height = '100cqw';
            // rotate and then restore position
            frameImageWrapper.style.transform = 'rotate(90deg) translateY(-100%)';
            frameImageWrapper.style.transformOrigin = 'top left';
            frameImageWrapper.append(frameImage);
            container.append(frameImageWrapper);
        } else {
            container.append(frameImage);
        }

        // Convert the inner rect coordinates to CSS top/right/down/left %s
        const innerRectInsets = [
            100 * relative.innerRect.y1,
            100 * (1 - relative.innerRect.x2),
            100 * (1 - relative.innerRect.y2),
            100 * relative.innerRect.x1
        ];
        const userImageInsetCss = (!this.#previewContext.landscape
            ? innerRectInsets
            : [innerRectInsets[3],...innerRectInsets.slice(0,-1)])
            .map((v) => `${v}%`).join(' ')

        // Add user image wrapper
        // Covers inner rect
        // (A wrapper is needed so that the image conforms to the dimensions when absolute-positioned)
        const userImageWrapper = document.createElement('div');
        userImageWrapper.style.position = 'absolute';
        userImageWrapper.style.inset = userImageInsetCss;
        userImageWrapper.style.zIndex = '1';
        container.append(userImageWrapper)

        // Add user image to wrapper
        // Covers the wrapper (inner rect)
        const userImage = document.createElement('img');
        userImage.src = this.#previewContext.userImage.url;
        userImage.style.width = '100%';
        userImage.style.height = '100%';
        userImage.style.objectFit = 'cover';
        userImageWrapper.append(userImage);

        return container;
    }

    /**
     * @returns {HTMLElement}
     */
    #renderScalePreview() {
        invariant(this.#previewContext);

        const relative = calculateRelativeRects(this.#previewContext.variantContext);

        // Render the standard preview
        const standardContainer = this.#renderStandardPreview();
        standardContainer.style.width = '100%';
        standardContainer.style.height = 'unset';

        // Create a container with the same aspect ratio as the preview-image
        const container = document.createElement('div');
        container.style.height = '100cqmin';
        container.style.aspectRatio = `${this.#previewContext.variantContext.scalePreview.image.width}/${this.#previewContext.variantContext.scalePreview.image.height}`;
        container.style.position = 'relative';
        container.style.contain = 'strict';

        // The container bounds establish a %-coordinate system for the rest of the
        // preview elements, where (0%, 0%) is the top-left of the container/preview-image
        // and (100%, 100%) is the bottom-right of the container/preview-image
        //
        // This simplifies the rest of the calculations and means layout doesn't need recalculating
        // when the page/container is resized.

        // Add preview image
        // Covers (0%, 0%) (100%, 100%)
        const previewImage = document.createElement('img');
        previewImage.src = this.#previewContext.variantContext.scalePreview.image.url;
        previewImage.style.width = '100%';
        previewImage.style.height = '100%';
        container.append(previewImage);

        // The standard preview needs to be centered on the scale preview position, but we need to take into
        // account that the frame itself may be offset inside the standard preview.

        // First, calculate the true center of the frame within the frame image
        const frameImageCenter = {
            x: (this.#previewContext.variantContext.frameSizing.outerRect[0] + (this.#previewContext.variantContext.frameSizing.outerRect[2] - this.#previewContext.variantContext.frameSizing.outerRect[0]) / 2) / this.#previewContext.variantContext.frameImage.width,
            y: (this.#previewContext.variantContext.frameSizing.outerRect[1] + (this.#previewContext.variantContext.frameSizing.outerRect[3] - this.#previewContext.variantContext.frameSizing.outerRect[1]) / 2) / this.#previewContext.variantContext.frameImage.height,
        };

        // Next, find the preview position in %-coordinates
        const previewPosition = {
            x: 100 * this.#previewContext.variantContext.scalePreview.position[0] / this.#previewContext.variantContext.scalePreview.image.width,
            y: 100 * this.#previewContext.variantContext.scalePreview.position[1] / this.#previewContext.variantContext.scalePreview.image.height,
        };

        // Add the standard preview and move to the preview position
        container.append(standardContainer);
        standardContainer.style.position = 'absolute';
        standardContainer.style.left = `${previewPosition.x}%`;
        standardContainer.style.top = `${previewPosition.y}%`;

        // Determine the proper scale of the frame within the preview
        // This needs to consider that the frame makes up only part of the frame-image, 
        const frameImageWidthMm = this.#previewContext.variantContext.frameSizing.outerSizeMillimetres[0] / (relative.outerRect.x2 - relative.outerRect.x1);
        const frameImageHeightMm = this.#previewContext.variantContext.frameSizing.outerSizeMillimetres[1] / (relative.outerRect.y2 - relative.outerRect.y1);
        const relativeScale = this.#previewContext.landscape
            ? frameImageHeightMm / this.#previewContext.variantContext.scalePreview.widthMillimetres
            : frameImageWidthMm / this.#previewContext.variantContext.scalePreview.widthMillimetres;

        // Center and scale the preview frame
        if (this.#previewContext.landscape) {
            standardContainer.style.transform = `
                translate(-${(1 - frameImageCenter.y) * 100}%, -${(1 - frameImageCenter.x) * 100}%)
                scale(${relativeScale})
            `;
        } else {
            standardContainer.style.transform = `
                translate(-${(1 - frameImageCenter.x) * 100}%, -${(1 - frameImageCenter.y) * 100}%)
                scale(${relativeScale})
            `;
        }

        return container;
    }
};
if (!customElements.get('frame-preview')) {
    customElements.define('frame-preview', FramePreviewElement);
}